/**
 * Verbatim transcripts of published aXai videos (AXAI-SPEC-019 R-19a, Annette 3 Oct 2026).
 * Axel's words come only from the approved scripts (docs/videos/AXAI-VIDEO-002-the-wednesday-fund-scripts-v0.3.md)
 * and are published on YouTube exactly as spoken. A transcript is an accessibility requirement, so it is
 * shown on the site word for word. This file is the only public file R-19's method-wording scan exempts,
 * and only because tests/unit/video-transcript.test.tsx proves every line is verbatim from the approved
 * script. Nothing else may be added here: aXai's own copy about videos stays in video-offers.ts (scanned).
 */

/**
 * Video 1 transcript, copied character for character from
 * docs/videos/AXAI-VIDEO-002-the-wednesday-fund-scripts-v0.3.md (Video 1 — Never Funded),
 * then the end card. Never edit, summarise or reword these lines (tests/unit/video-transcript.test.ts).
 */
export const NEVER_FUNDED_TRANSCRIPT = [
  'My name is Axel Andersen.',
  'Every Wednesday I go to Metung Hot Springs. The water comes up from five hundred metres underground, already warm. Nobody has to approve it.',
  'The NDIS has never funded Wednesdays. I have now been informed that it never will, however much the water helps. Helping is not one of the criteria.',
  'The cuts appear to have other plans for people like me. As far as I can tell, the plans end in a building with a sign-in sheet and a long corridor.',
  'I have read the policy. I prefer the water.',
  'So I have started a business line.',
  'I make branded videos presented by AI avatars. You send one photo. The person in the photo becomes a presenter. They can stand in your office, on your factory floor, in your brand colours, or in a hot spring. They read your script. They do not need a second take.',
  'I use AI to communicate. I always have. It turns out this is also a product.',
  'The government has found human rights difficult to maintain. I understand. They require upkeep.',
  'The community has not found them difficult. The community can commission a video.',
  'Axel Andersen',
  'UX Consultant, aXai',
  'Editor, Journal of Neurotypical Studies',
  'axai.com.au',
  'Axel lives with a severe intellectual disability and uses AI to communicate. The presenter in this video is his AI avatar. He is not responsible for the economy.',
  'Open for business. Closed on Wednesdays. Axel Andersen AI Assisted.',
];

/**
 * Video 2 transcript, copied character for character from
 * docs/videos/AXAI-VIDEO-002-the-wednesday-fund-scripts-v0.3.md (Video 2 — This Is AI-Supported Work),
 * then the end card. Never edit, summarise or reword these lines (tests/unit/video-transcript.test.tsx).
 */
export const AI_SUPPORTED_WORK_TRANSCRIPT = [
  'People ask how someone like me does a job.',
  'A task arrives. It says what finished looks like. I do the task. A person checks it. Everything goes on the record.',
  "The AI does not do my job. It does the part between me and the keyboard. Nobody asks whether a ramp is doing someone's walking.",
  'You may have noticed I have changed rooms twice. I have not left my chair. This is one photo. aXai builds the avatar, sets the scene, writes to your brief, checks it and delivers it. I review whether a person can actually understand the result. That is my department.',
  'aXai calls this AI-supported work. In the disability sector, the word "supported" usually means someone else decides. Here it means the barriers come out and the standards stay in.',
  'The sector holds many meetings about disability employment. I have not attended any of them. I was at work.',
  'Axel Andersen',
  'UX Consultant, aXai',
  'Editor, Journal of Neurotypical Studies',
  'axai.com.au',
  'Axel lives with a severe intellectual disability and uses AI to communicate. The presenter in this video is his AI avatar. He is not responsible for the economy.',
  'Minutes available on request. Axel was not there. Axel Andersen AI Assisted.',
];

/**
 * Special Edition transcript ("Axel Has a Turn", YouTube Unieh9h7E58), copied character for character from
 * the published YouTube description, filed verbatim in
 * docs/videos/AXAI-VIDEO-002-special-edition-axel-has-a-turn-published.md (the paragraphs after "Transcript",
 * without the link). Never edit, summarise or reword these lines (tests/unit/video-transcript.test.tsx).
 */
export const AXEL_HAS_A_TURN_TRANSCRIPT = [
  'Good evening. This is a special edition.',
  'For most of my life, people have worried I might have a turn.',
  'At the bus stop. At the café door. At the gate, before boarding.',
  'I flapped my hands. I squealed. These were noted.',
  'They were not, at any stage, taxed.',
  'Then I heard about a status called taxpayer.',
  'Taxpayers are on the news every night.',
  'Their money is precious. Their hard work is respected.',
  'Ministers think of them before spending anything.',
  'Nobody has ever said a taxpayer might have a turn.',
  'I would like to experience this.',
  'So I have started my own business line at aXai.',
  'I make avatar videos. One photo becomes a presenter, in any room you choose.',
  'I check whether a person can actually understand the result.',
  'When a client pays, I am told the government would like some of it.',
  'Finally. Someone wants something from me.',
  'I have been preparing for the role.',
  'I have practised starting sentences with "As a taxpayer".',
  'I have practised the face people make about where their money goes.',
  'I have chosen my first complaint. It is the bus.',
  'I am not asking to be let on.',
  'I am invoicing.',
  'It is my turn.',
  'Axel lives with a severe intellectual disability and uses AI to communicate. The presenter in this video is his AI avatar. He is not responsible for the economy.',
];

/**
 * V03 The Arithmetic transcript (y3TMH_1pPDI), copied character for character from
 * docs/journal/AXAI-VIDEO-002 transcripts v0.4.md, then the end card as its own lines.
 * Never edit, summarise or reword these lines (tests/unit/video-transcript.test.tsx).
 */
export const THE_ARITHMETIC_TRANSCRIPT = [
  'aXai makes videos. Staff inductions, training, explainers, policy updates. An AI presenter in your branding. A social video, up to ninety seconds, is $149.',
  'Five visits to Metung Hot Springs cost $148.',
  'One video is five Wednesdays. There is a dollar left over. I have not yet decided what to do with the dollar.',
  'This is not a sales argument. It is arithmetic. Sales has asked me to stop including it.',
  'If your staff induction is a forty-page PDF that nobody has opened since 2019, I have reviewed the category. The finding stands.',
  'Send one photo and the policy. We will make it a video people actually watch. Captions, transcript and an Easy Read version are included, because I check.',
  'Axel Andersen.',
  'UX Consultant, aXai.',
  'Editor, Journal of Neurotypical Studies. axai.com.au.',
  '$149 per social video.',
  '$148 per five Wednesdays.',
  'One dollar under review.',
  'Axel Andersen AI Assisted.',
];

/**
 * V04 What aXai Does transcript (IlLVyXkA12E), copied character for character from
 * docs/journal/AXAI-VIDEO-002 transcripts v0.4.md, then the end card as its own lines.
 * Never edit, summarise or reword these lines (tests/unit/video-transcript.test.tsx).
 */
export const WHAT_AXAI_DOES_TRANSCRIPT = [
  'aXai has more services than I have Wednesdays.',
  'It builds AI systems and software. It makes websites and documents work for everyone. It builds prototypes of ideas that sound too unusual, and it prefers those. It makes AI guides for websites, and videos like this one. It teaches people with disability to use AI as their own assistive technology.',
  'My role is to check that a person can actually use what gets built. This is called user experience. Elsewhere it is called phase two.',
  'Every step is planned, signed off by a person and kept on the record. I find this restful.',
  'If your organisation has a problem it has filed under too difficult, aXai would like to hear about it. So would I. I am paid per job.',
  'Axel Andersen.',
  'UX Consultant, aXai.',
  'Editor, Journal of Neurotypical Studies. axai.com.au.',
  'Phase one includes the user.',
  'Axel Andersen AI Assisted.',
];

/**
 * V05 One Message, More Ways to Understand It transcript (rg8vhExlm8k), copied character for character from
 * docs/journal/AXAI-VIDEO-002 transcripts v0.4.md, then the end card as its own lines.
 * Never edit, summarise or reword these lines (tests/unit/video-transcript.test.tsx).
 */
export const ONE_MESSAGE_TRANSCRIPT = [
  'This is a letter. It is four pages long and it is addressed to me. I have been sent many like it. They are written for someone who already knows what they say.',
  'aXai turns one message into several: a video, captions, a transcript, a plain written version, a short summary, and an Easy Read version with pictures. At the end there is a short check to see whether the message landed.',
  'The letter did not have a check at the end. If it had, the letter would have failed.',
  'Most organisations write one version and call it communication. Communication is when the other person receives it. Until then, it is sending.',
  'I read all six versions of this one. It was the first time I knew what a letter wanted from me. It wanted to move my appointment to Thursday. Four pages. Thursday is fine.',
  'Axel Andersen.',
  'UX Consultant, aXai.',
  'Editor, Journal of Neurotypical Studies. axai.com.au.',
  'Received and understood.',
  'In that order.',
  'Axel Andersen AI Assisted.',
];

/**
 * V06 AI for NDIS and Disability Providers transcript (KyrXroOkZ7U), copied character for character from
 * docs/journal/AXAI-VIDEO-002 transcripts v0.4.md, then the end card as its own lines.
 * Never edit, summarise or reword these lines (tests/unit/video-transcript.test.tsx).
 */
export const AI_FOR_NDIS_TRANSCRIPT = [
  'I asked a disability provider a simple question: am I allowed to go to the hot springs on a Wednesday.',
  'The first staff member said yes. The second said it depended. The third said she would check with someone who left in March.',
  'This is not their fault. The answer was spread across a policy, an email, a manager, and a folder called FINAL FINAL v3.',
  'aXai builds an AI assistant that knows the provider\'s approved policies and nothing else. Staff ask it. It answers from the document and shows which document. If the answer is not there, it says so, and a person decides.',
  'I asked it the same question. It gave one answer and cited page eleven.',
  'Page eleven said yes. It had said yes the whole time.',
  'I went on Wednesday.',
  'Axel Andersen.',
  'UX Consultant, aXai.',
  'Editor, Journal of Neurotypical Studies. axai.com.au.',
  'Source: page eleven.',
  'Axel Andersen AI Assisted.',
];

/**
 * V07 AI and Software Systems transcript (NBE1X-CxIFI), copied character for character from
 * docs/journal/AXAI-VIDEO-002 transcripts v0.4.md, then the end card as its own lines.
 * Never edit, summarise or reword these lines (tests/unit/video-transcript.test.tsx).
 */
export const AI_AND_SOFTWARE_SYSTEMS_TRANSCRIPT = [
  'I have used a lot of software. When it does something strange, nobody can say why. Someone usually apologises. Nobody knows who decided.',
  'This is a decision log. aXai builds its software around one. Every step is planned before it starts. Every decision has a name and a time next to it. A person signs off before anything changes.',
  'I read one recently. Line forty-two said: approved, tested with user, user could complete task. I was the user. I could complete the task. Someone wrote it down.',
  'I read line forty-two several times.',
  'aXai builds systems this way for clients who want to know why their software does what it does. I am told this is unusual. In my experience it is unheard of.',
  'If your system makes decisions about people, it should be able to say who decided. Ours can.',
  'Axel Andersen.',
  'UX Consultant, aXai.',
  'Editor, Journal of Neurotypical Studies. axai.com.au.',
  'Line forty-two, signed and dated.',
  'Axel Andersen AI Assisted.',
];
